import * as React from "react"
import { Radio as RadioPrimitive } from "@base-ui/react/radio"
import { RadioGroup as RadioGroupPrimitive } from "@base-ui/react/radio-group"

import { cn } from "@/frontend/lib/utils"

type RadioGroupVariant = "default" | "pills"

const RadioGroupContext = React.createContext<{
  variant: RadioGroupVariant
  size: "default" | "sm" | "lg"
}>({
  variant: "default",
  size: "default",
})

function RadioGroup<Value = any>({
  className,
  variant = "default",
  size = "default",
  children,
  ...props
}: RadioGroupPrimitive.Props<Value> & {
  variant?: RadioGroupVariant
  size?: "default" | "sm" | "lg"
}) {
  return (
    <RadioGroupPrimitive
      data-slot="radio-group"
      className={cn(
        variant === "default" && "grid w-full gap-2",
        variant === "pills" &&
          "inline-flex w-fit items-center rounded-lg",
        className
      )}
      {...props}
    >
      <RadioGroupContext.Provider value={{ variant, size }}>
        {children}
      </RadioGroupContext.Provider>
    </RadioGroupPrimitive>
  )
}

function RadioGroupItem<Value = any>({
  className,
  children,
  ...props
}: RadioPrimitive.Root.Props<Value>) {
  const { variant, size } = React.useContext(RadioGroupContext)

  if (variant === "pills") {
    return (
      <RadioPrimitive.Root
        data-slot="radio-group-item"
        nativeButton
        render={<button type="button" />}
        className={cn(
          "inline-flex flex-1 cursor-pointer items-center justify-center gap-1 whitespace-nowrap border border-input bg-transparent font-medium transition-all outline-none",
          // Remove duplicate borders between adjacent items
          "rounded-none border-l-0 first:rounded-l-lg first:border-l last-of-type:rounded-r-lg",
          "hover:bg-muted hover:text-foreground",
          "focus-visible:z-10 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
          "data-disabled:cursor-not-allowed data-disabled:opacity-50",
          "data-checked:bg-muted data-checked:text-foreground",
          size === "sm" && "h-7 px-1.5 text-sm",
          size === "default" && "h-8 px-2",
          size === "lg" && "h-9 px-2.5",
          className
        )}
        {...props}
      >
        {children}
      </RadioPrimitive.Root>
    )
  }

  return (
    <RadioPrimitive.Root
      data-slot="radio-group-item"
      className={cn(
        "group/radio-group-item peer relative flex aspect-square size-4 shrink-0 rounded-full border border-input outline-none after:absolute after:-inset-x-3 after:-inset-y-2 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 data-disabled:cursor-not-allowed data-disabled:opacity-50 aria-invalid:border-destructive-fg aria-invalid:ring-3 aria-invalid:ring-destructive-border aria-invalid:aria-checked:border-primary dark:bg-input/30 dark:aria-invalid:border-destructive-fg/50 dark:aria-invalid:ring-destructive-border data-checked:border-primary data-checked:bg-primary data-checked:text-primary-foreground dark:data-checked:bg-primary",
        className
      )}
      {...props}
    >
      <RadioPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="flex size-4 items-center justify-center"
      >
        <span className="absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-foreground" />
      </RadioPrimitive.Indicator>
    </RadioPrimitive.Root>
  )
}

export { RadioGroup, RadioGroupItem }
